import { useState } from 'react'
import { Search, Plus, Pencil, Trash2, Shield, ShieldCheck, ShieldOff } from 'lucide-react'
import { Modal, ConfirmModal } from '../common/Modal'

const ADMINS = [
  { id:'SA-001', name:'Rohan Verma',   email:'rohan@admin.com',  perms:['Users','Reports','Settings'], status:'active',   last:'2 min ago' },
  { id:'SA-002', name:'Anjali Mehta',  email:'anjali@admin.com', perms:['Users','Items'],              status:'active',   last:'1 hr ago' },
  { id:'SA-003', name:'Carlos Rivera', email:'carlos@admin.com', perms:['Reports'],                    status:'inactive', last:'3 days ago' },
  { id:'SA-004', name:'Mei Lin',       email:'mei@admin.com',    perms:['Users','Items','Reports'],    status:'active',   last:'5 min ago' },
]

export default function SubAdmin() {
  const [admins, setAdmins] = useState(ADMINS)
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState({ name: '', email: '', perms: 'Users,Reports', status: 'active' })

  const filtered = admins.filter(a => a.name.toLowerCase().includes(search.toLowerCase()))

  const openAdd = () => {
    setActive(null)
    setForm({ name: '', email: '', perms: 'Users,Reports', status: 'active' })
    setModalOpen(true)
  }

  const openEdit = (admin) => {
    setActive(admin)
    setForm({ name: admin.name, email: admin.email, perms: admin.perms.join(','), status: admin.status })
    setModalOpen(true)
  }

  const save = () => {
    if (!form.name.trim() || !form.email.trim()) return
    const payload = { ...form, perms: form.perms.split(',').map(p => p.trim()).filter(Boolean) }
    if (active) {
      setAdmins(prev => prev.map(a => a.id === active.id ? { ...a, ...payload } : a))
    } else {
      const id = `SA-${String(admins.length + 1).padStart(3, '0')}`
      setAdmins(prev => [{ id, ...payload, last: 'just now' }, ...prev])
    }
    setModalOpen(false)
  }

  const onDelete = () => {
    setAdmins(prev => prev.filter(a => a.id !== active.id))
    setDeleteOpen(false)
    setActive(null)
  }

  const activeCount   = admins.filter(a => a.status === 'active').length
  const inactiveCount = admins.filter(a => a.status === 'inactive').length

  const stats = [
    { label: 'Total Sub Admins', value: admins.length,  icon: Shield,      color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', trend: '+12%',  bar: 100 , trendColor: '#4F46E5',variant: 'indigo' },
    { label: 'Active',           value: activeCount,   icon: ShieldCheck, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', trend: '+18%',  bar: Math.round(activeCount/admins.length*100), trendColor: '#10B981',variant: 'green' },
    { label: 'Inactive',         value: inactiveCount, icon: ShieldOff,   color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', trend: '0%',   bar: Math.round(inactiveCount/admins.length*100), trendDir: 'down', trendColor: '#EF4444',variant: 'red' },
  ]

  return (
    <div className="dash-page page-in">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'var(--space-md)' }}>
        <h1 className="page-title">Sub Admins</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14}/> Add Sub Admin</button>
      </div>

      <div className="stats-grid cols-3">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{"position": "absolute", "top": "0px", "right": "0px", "width": "110px", "height": "110px", "border-radius": "50%", "background": s.colorGlow  , "filter": "blur(30px)", "transform": "translate(30%, -30%)", "pointer-events": "none"}}></span>   
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8}/>
              </div>
              <span className={`stat-card-trend${s.trendDir === 'down' ? ' down' : s.trendDir === 'neutral' ? ' neutral' : ''}`} style={{ color: s.trendColor, background: `${s.trendColor}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.trend}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ "position": "absolute", "bottom": "0px", "left": "0px", "right": "0px", "height": "2px", background: `linear-gradient(90deg,${s.color}88,transparent)`, "border-radius": "0 0 var(--radius-lg) var(--radius-lg)" }}></div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'var(--space-sm)', padding:'var(--space-md) var(--space-lg)', borderBottom:'1px solid var(--border)' }}>
          <div style={{ position:'relative', flex:1, minWidth:180 }}>
            <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text3)' }}/>
            <input className="input" style={{ paddingLeft:32 }} placeholder="Search sub admins…" value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Permissions</th><th>Status</th><th>Last Active</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--color-purple-glow)', color:'var(--color-purple)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'var(--fs-xs)', fontWeight:700, flexShrink:0 }}>
                        {a.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight:600, color:'var(--text1)', fontSize:'var(--fs-sm)' }}>{a.name}</div>
                        <div style={{ fontSize:'var(--fs-xs)', color:'var(--text3)' }}>{a.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                      {a.perms.map(p => <span key={p} className="badge badge-gold">{p}</span>)}
                    </div>
                  </td>
                  <td><span className={`badge ${a.status==='active'?'badge-active':'badge-inactive'}`}>{a.status}</span></td>
                  <td style={{ color:'var(--text3)', fontSize:'var(--fs-xs)' }}>{a.last}</td>
                  <td>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(a)}><Pencil size={14}/></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(a); setDeleteOpen(true) }}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit Sub Admin' : 'Add Sub Admin'}
        subtitle="Manage sub-admin identity and permissions"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>{active ? 'Update' : 'Create'}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-field">
            <label>Sub Admin Name</label>
            <input className="input" placeholder="Enter name" value={form.name} onChange={e => setForm({...form, name:e.target.value})}/>
          </div>
          <div className="form-field">
            <label>Email</label>
            <input className="input" placeholder="Enter email" value={form.email} onChange={e => setForm({...form, email:e.target.value})}/>
          </div>
          <div className="form-field">
            <label>Permissions</label>
            <input className="input" placeholder="Users, Reports, Settings" value={form.perms} onChange={e => setForm({...form, perms:e.target.value})}/>
          </div>
          <div className="form-field">
            <label>Status</label>
            <select className="input" value={form.status} onChange={e => setForm({...form, status:e.target.value})}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete sub admin"
        message={`Delete ${active?.name || 'selected sub admin'}?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}